import 'twin.macro'

import TwoCardsIcon from 'assets/icons/TwoCardsIcon'
import { Button } from 'components/UI/button/button'
import Modal from 'components/UI/modal/modal'
import SubBody from 'components/UI/sub-body/sub-body'
import SubTitle from 'components/UI/sub-title/sub-title'
import Title from 'components/UI/title/title'
import dayjs from 'dayjs'
import { Card } from 'pages/UserPersonalCabinet'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { ClipLoader } from 'react-spinners'
import { useSendSmsMutation } from 'redux/api/lk.api'
import { mobilePhoneSelector } from 'redux/store/slices/application-slice'
import tw from 'twin.macro'
import { pathnames } from 'utils/steps'

import GiftTimer from '../gift-timer'
import CardImg from './../../../assets/images/mega_syiliq_card_img.webp'
import CancelGiftModal from './cancel-gift-modal'
import GiftCardFields from './gift-card-fields'

function CardContainer({ data, refetchProducts }: { data: Card; refetchProducts: any }) {
  const [openRequisites, setOpenRequisites] = useState<boolean>(false)
  const [openGift, setOpenGift] = useState<boolean>(false)
  const [openCancelConfirm, setOpenCancelConfirm] = useState<boolean>(false)

  const mobilePhone = useSelector(mobilePhoneSelector)

  const [sendSms, { isSuccess, isLoading, error }] = useSendSmsMutation()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleCloseRequisites = () => setOpenRequisites(false)
  const handleCloseGift = () => setOpenGift(false)
  const handleOpenGift = () => setOpenGift(true)

  const handleSendSms = async () => {
    try {
      await sendSms({ uuid: data.uuid }).unwrap()
    } catch (e) {
      setOpenRequisites(true)
    }
  }

  const isGift = useMemo(() => {
    if (data.gift_date === '-') return true
    return dayjs().isBefore(dayjs(data.gift_date).add(1, 'day'))
  }, [data.gift_date])

  const hasPendingGift = data.date_of_gift !== '-' && data.gift_date === ''

  const canCancelGift = data.gift_date !== '-'
  useEffect(() => {
    if (isSuccess) setOpenRequisites(true)
  }, [isSuccess])

  return (
    <>
      <div tw='bg-secondary p-8 rounded-s20 w-full flex flex-col items-center gap-5 min-h-[368px]'>
        <div tw='w-full bg-primary flex rounded-s14 p-4 justify-between h-full'>
          <div tw='h-full flex flex-col justify-between'>
            <section>
              <SubBody twStyle={tw`font-medium text-secondary`}>{t('card.card_number', { ns: 'cabinet' })}</SubBody>
              <SubTitle twStyle={tw`font-medium`}>{data.number_card}</SubTitle>
            </section>
            <section>
              <SubBody twStyle={tw`font-medium text-secondary`}>{t('card.validity', { ns: 'cabinet' })}</SubBody>
              <SubTitle twStyle={tw`font-medium`}>{data.expiry_date}</SubTitle>
            </section>
          </div>
          <div>
            <img src={CardImg} alt='' width={70} />
          </div>
        </div>

        <Title variant='bold'>{Number(data.balance).toLocaleString()} ₸</Title>

        <div tw='w-full flex flex-col gap-3'>
          {data.gift_date === '-' && (
            <Button variant='gradient' onClick={handleOpenGift}>
              {t('card.give_card', { ns: 'cabinet' })}
            </Button>
          )}
          {data.gift_date === '-' && (
            <div tw='flex gap-1 mt-2'>
              <button
                tw='w-full bg-primary py-3 px-3 rounded-s32 text-s14 font-medium h-[40px]'
                onClick={handleSendSms}
              >
                {isLoading ? <ClipLoader size={20} /> : t('card.requisites', { ns: 'cabinet' })}
              </button>
              <button
                tw='w-full bg-primary py-3 px-3 rounded-s32 text-s14 font-medium h-[40px]'
                onClick={() => navigate(pathnames.statement, { state: { cardInfo: data } })}
              >
                {t('card.statement', { ns: 'cabinet' })}
              </button>
            </div>
          )}

          {data.date_of_gift !== '-' && data.gift_message !== null && data.gift_date !== '-' && (
            <p tw='text-s14 text-secondary text-center'>
              {t('pending_gift_info', {
                ns: 'gift',
                datetime: dayjs(data.date_of_gift).format('DD.MM.YYYY HH:mm')
              })}
            </p>
          )}

          {/* Congratulatory message from sender */}
          {data.gift_message && (
            <div tw='p-3 bg-primary rounded-s14 flex flex-col gap-1'>
              <SubBody twStyle={tw`text-secondary text-s12`}>{t('gift_message_label', { ns: 'gift' })}</SubBody>
              <p tw='text-s14 font-medium break-words'>{data.gift_message}</p>
            </div>
          )}

          {/* Deferred gift info */}
          {canCancelGift && (
            <>
              <GiftTimer gift_date={data.gift_date} />
              <button
                tw='w-full bg-primary border border-red-500 py-3 rounded-s14 text-s14 font-semibold text-red-500'
                onClick={() => setOpenCancelConfirm(true)}
              >
                {t('cancel_gift', { ns: 'gift' })}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Requisites modal */}
      <Modal open={openRequisites} onClose={handleCloseRequisites} twStyle={tw`max-w-[380px]`} withClose={false}>
        <div tw='flex flex-col items-center text-center gap-5'>
          {error ? (
            <Title variant='bold'>{error?.data?.error?.description}</Title>
          ) : (
            <>
              <TwoCardsIcon />
              <Title variant='bold'>{t('requisites.title', { ns: 'cabinet' })}</Title>
              <SubTitle twStyle={tw`text-secondary`}>
                {t('requisites.desc', { ns: 'cabinet', phone: mobilePhone })}
              </SubTitle>
              <Button variant='gradient' onClick={handleCloseRequisites}>
                {t('requisites.close', { ns: 'cabinet' })}
              </Button>
            </>
          )}
        </div>
      </Modal>

      {/* Gift modal */}
      <Modal open={openGift} onClose={handleCloseGift} withBackdrop={false}>
        <GiftCardFields
          uuid={data.uuid}
          expiry_date={data.expiry_date}
          onClose={handleCloseGift}
          refetchProducts={refetchProducts}
        />
      </Modal>

      {/* Cancel deferred gift confirmation modal */}
      <Modal
        open={openCancelConfirm}
        onClose={() => setOpenCancelConfirm(false)}
        twStyle={tw`max-w-[422px]`}
        withClose={false}
      >
        <CancelGiftModal
          key={String(openCancelConfirm)}
          uuid={data.uuid}
          executionDate={data.date_of_gift}
          onClose={() => setOpenCancelConfirm(false)}
          onSuccess={() => {
            refetchProducts()
          }}
        />
      </Modal>
    </>
  )
}

export default CardContainer
